import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert, Button } from 'react-bootstrap';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Hook for telemetry (App Insights, etc.)
    console.error('Unhandled UI error:', error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <Alert variant="danger" className="m-4">
          <Alert.Heading>Something went wrong</Alert.Heading>
          <p>{this.state.error?.message ?? 'An unexpected error occurred.'}</p>
          <Button variant="outline-danger" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </Alert>
      );
    }
    return this.props.children;
  }
}
